import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, Button, TextField, List, ListItem, ListItemText, IconButton, Paper, Divider, Badge, Chip, MenuItem, Select, InputLabel, FormControl, CircularProgress, Alert } from '@mui/material';
import { 
  ThumbUp, 
  Delete, 
  Assignment, 
  Chat,
  Add,
  Send,
  Category,
  Group
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { collection, addDoc, deleteDoc, onSnapshot, doc, updateDoc, getDoc } from 'firebase/firestore';
import { firestore } from '../config/firebase';
import { AuthContext } from '../contexts/AuthContext';

interface Idea {
  id: string;
  text: string;
  cluster: string;
  votes: number;
  voters: string[];
  userId: string;
  userName: string;
}

const mockUserId = 'user1'; // Replace with real user ID from auth later

const IdeationPhase: React.FC = () => {
  const { teamId, sprintId } = useParams<{ teamId: string; sprintId: string }>();
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;

  // Ideas
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [newIdea, setNewIdea] = useState('');
  const [newCluster, setNewCluster] = useState('');
  const [clusters, setClusters] = useState<string[]>([]);
  const [ideaLoading, setIdeaLoading] = useState(false);
  const [ideaError, setIdeaError] = useState<string | null>(null);

  // Comments
  const [comments, setComments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);

  // Firestore listeners
  useEffect(() => {
    if (!teamId || !sprintId) return;
    // Ideas
    const unsubIdeas = onSnapshot(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/ideation_ideas`), snap => {
      const ideasData = snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Idea));
      setIdeas(ideasData);
      // Update clusters from ideas
      const uniqueClusters = Array.from(new Set(ideasData.map(i => i.cluster).filter(Boolean)));
      setClusters(uniqueClusters);
    });
    // Comments
    const unsubComments = onSnapshot(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/ideation_comments`), snap => {
      setComments(snap.docs.map(doc => doc.data() as any));
    });
    return () => {
      unsubIdeas();
      unsubComments();
    };
  }, [teamId, sprintId]);

  // Add Idea
  const handleAddIdea = async () => {
    if (newIdea.trim() && teamId && sprintId) {
      setIdeaLoading(true);
      setIdeaError(null);
      try {
        const cluster = newCluster.trim() || 'Ungrouped';
        await addDoc(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/ideation_ideas`), {
          text: newIdea.trim(),
          cluster,
          votes: 0,
          voters: [],
          userId: currentUser?.uid || 'unknown',
          userName: currentUser?.displayName || currentUser?.email || 'Unknown',
        });
        setNewIdea('');
        setNewCluster('');
      } catch (err) {
        setIdeaError('Failed to add idea.');
      }
      setIdeaLoading(false);
    }
  };

  // Vote Idea
  const handleVote = async (id: string) => {
    if (!teamId || !sprintId) return;
    setIdeaLoading(true);
    setIdeaError(null);
    try {
      const ideaRef = doc(firestore, `teams/${teamId}/designSprints/${sprintId}/ideation_ideas`, id);
      const ideaSnap = await getDoc(ideaRef);
      if (ideaSnap.exists()) {
        const data = ideaSnap.data();
        if (!data.voters.includes(mockUserId)) {
          await updateDoc(ideaRef, {
            votes: (data.votes || 0) + 1,
            voters: [...(data.voters || []), mockUserId]
          });
        }
      }
    } catch (err) {
      setIdeaError('Failed to vote.');
    }
    setIdeaLoading(false);
  };

  // Delete Idea
  const handleDeleteIdea = async (id: string) => {
    if (!teamId || !sprintId) return;
    try {
      await deleteDoc(doc(firestore, `teams/${teamId}/designSprints/${sprintId}/ideation_ideas`, id));
    } catch (err) {
      setIdeaError('Failed to delete idea.');
    }
  };

  // Comments
  const handleAddComment = async () => {
    if (newComment.trim() && teamId && sprintId) {
      setCommentLoading(true);
      setCommentError(null);
      try {
        await addDoc(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/ideation_comments`), {
          text: newComment.trim(),
          userId: currentUser?.uid || 'unknown',
          userName: currentUser?.displayName || currentUser?.email || 'Unknown',
        });
        setNewComment('');
      } catch (err) {
        setCommentError('Failed to add comment.');
      }
      setCommentLoading(false);
    }
  };

  // Group ideas by cluster
  const ideasByCluster: { [cluster: string]: Idea[] } = {};
  ideas.forEach(idea => {
    if (!ideasByCluster[idea.cluster]) ideasByCluster[idea.cluster] = [];
    ideasByCluster[idea.cluster].push(idea);
  });

  return (
    <div className="space-y-6">
      {/* Idea Submission Section */}
      <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-6 border border-indigo-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-indigo-500 rounded-xl flex items-center justify-center">
            <Assignment className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 font-['Poppins']">Submit Ideas</h3>
            <p className="text-gray-600 text-sm font-['Inter']">Share and organize your creative ideas</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 mb-4">
          <div className="lg:col-span-2">
            <TextField
              label="Your Idea"
              value={newIdea}
              onChange={e => setNewIdea(e.target.value)}
              size="small"
              fullWidth
              disabled={ideaLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  fontFamily: 'Inter, sans-serif',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#6366f1'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#6366f1',
                    borderWidth: '2px'
                  }
                }
              }}
              placeholder="Describe your idea..."
              onKeyPress={(e) => e.key === 'Enter' && handleAddIdea()}
            />
          </div>
          
          <div>
            <FormControl fullWidth size="small" sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontFamily: 'Inter, sans-serif'
              }
            }}>
              <InputLabel>Cluster</InputLabel>
              <Select
                value={newCluster}
                label="Cluster"
                onChange={e => setNewCluster(e.target.value)}
                renderValue={selected => selected || 'Ungrouped'}
                disabled={ideaLoading}
              >
                <MenuItem value=""><em>Ungrouped</em></MenuItem>
                {clusters.map(cluster => (
                  <MenuItem key={cluster} value={cluster}>{cluster}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          
          <div>
            <Button
              variant="contained"
              onClick={handleAddIdea}
              disabled={ideaLoading || !newIdea.trim()}
              startIcon={<Add />}
              fullWidth
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                borderRadius: '12px',
                py: 1.5,
                color: '#ffffff !important',
                background: 'linear-gradient(135deg, #6366f1 0%, #3b82f6 100%) !important',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5855eb 0%, #2563eb 100%) !important',
                  color: '#ffffff !important'
                },
                '&:disabled': {
                  background: '#d1d5db',
                  color: '#6b7280'
                }
              }}
            >
              Submit
            </Button>
          </div>
        </div>

        {ideaLoading && (
          <div className="flex items-center gap-2 text-indigo-600">
            <CircularProgress size={16} />
            <span className="text-sm font-['Inter']">Submitting idea...</span>
          </div>
        )}
        
        {ideaError && (
          <Alert severity="error" sx={{ borderRadius: '12px', fontFamily: 'Inter, sans-serif' }}>
            {ideaError}
          </Alert>
        )}
      </div>

      {/* Ideas by Cluster */}
      {Object.keys(ideasByCluster).map(cluster => (
        <div key={cluster} className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-6 border border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-slate-500 rounded-lg flex items-center justify-center">
              <Group className="w-4 h-4 text-white" />
            </div>
            <div className="flex items-center gap-2">
              <Chip 
                label={cluster} 
                color={cluster === 'Ungrouped' ? 'default' : 'primary'} 
                size="small"
                sx={{ 
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500
                }}
              />
              <span className="text-sm text-gray-600 font-['Inter']">
                {ideasByCluster[cluster].length} idea{ideasByCluster[cluster].length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
          
          <div className="space-y-3">
            {ideasByCluster[cluster].map(idea => (
              <div key={idea.id} className="bg-white rounded-xl p-4 border border-gray-200 hover:shadow-md transition-all duration-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-gray-900 font-['Inter'] font-medium">{idea.text}</p>
                    <p className="text-sm text-gray-500 font-['Inter'] mt-1">by {idea.userName || 'Unknown'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant={idea.voters.includes(mockUserId) ? "contained" : "outlined"}
                      size="small"
                      startIcon={<ThumbUp />}
                      onClick={() => handleVote(idea.id)}
                      disabled={idea.voters.includes(mockUserId) || ideaLoading}
                      sx={{
                        borderRadius: '8px',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.75rem',
                        minWidth: 'auto',
                        px: 2,
                        color: idea.voters.includes(mockUserId) ? '#ffffff' : '#6366f1',
                        borderColor: '#6366f1',
                        ...(idea.voters.includes(mockUserId) && {
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          color: '#ffffff',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                            color: '#ffffff'
                          }
                        })
                      }}
                    >
                      {idea.votes}
                    </Button>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteIdea(idea.id)}
                      disabled={ideaLoading}
                      sx={{
                        color: '#ef4444',
                        '&:hover': { backgroundColor: '#fef2f2' }
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {ideas.length === 0 && (
        <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-6 border border-slate-100 text-center">
          <Assignment className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="font-['Inter'] text-gray-500">No ideas submitted yet. Be the first to share your creative thoughts!</p>
        </div>
      )}

      {/* Comments Section */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center">
            <Chat className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 font-['Poppins']">Team Discussion</h3>
            <p className="text-gray-600 text-sm font-['Inter']">Share thoughts and collaborate</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <TextField
            label="Write a comment..."
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            size="small"
            fullWidth
            disabled={commentLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontFamily: 'Inter, sans-serif',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#8b5cf6'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#8b5cf6',
                  borderWidth: '2px'
                }
              }
            }}
            placeholder="Share your thoughts..."
            onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
          />
          <Button
            variant="contained"
            onClick={handleAddComment}
            disabled={commentLoading || !newComment.trim()}
            startIcon={<Send />}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              borderRadius: '12px',
              px: 3,
              py: 1.5,
              color: '#ffffff !important',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%) !important',
              '&:hover': {
                background: 'linear-gradient(135deg, #7c3aed 0%, #db2777 100%) !important',
                color: '#ffffff !important'
              },
              '&:disabled': {
                background: '#d1d5db',
                color: '#6b7280'
              }
            }}
          >
            Send
          </Button>
        </div>

        {commentLoading && (
          <div className="flex items-center gap-2 text-purple-600">
            <CircularProgress size={16} />
            <span className="text-sm font-['Inter']">Sending comment...</span>
          </div>
        )}
        
        {commentError && (
          <Alert severity="error" sx={{ borderRadius: '12px', fontFamily: 'Inter, sans-serif' }}>
            {commentError}
          </Alert>
        )}

        <div className="space-y-3">
          {comments.map((comment, idx) => (
            <div key={idx} className="bg-white rounded-xl p-4 border border-gray-200">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center text-white font-['Inter'] font-semibold">
                  {(comment.userName || 'U').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="text-gray-900 font-['Inter']">{comment.text}</p>
                  <p className="text-sm text-gray-500 font-['Inter'] mt-1">by {comment.userName || 'Unknown'}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {comments.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Chat className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-['Inter']">No comments yet. Start the discussion!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default IdeationPhase; 